import { clearAuthClientState, getCookie } from '../utils/cookie'
import { isLoginDomain } from '../utils/environment'
import { getResponseErrorMessage } from '../utils/errors'
import { isLocalhost } from '../utils/appIdStorage'

// Flag to prevent multiple modal windows from showing
let isSessionExpiredModalShown = false;

// Pre-import action creator to avoid async delay
let showSessionExpiredModalAction = null;
let sessionExpiredSliceModule = null;

// Synchronously import and cache the slice module
const initSessionExpiredModal = () => {
  if (!sessionExpiredSliceModule && typeof window !== 'undefined' && window.__REDUX_STORE__) {
    import("../redux/features/auth/sessionExpiredSlice")
      .then((sliceModule) => {
        sessionExpiredSliceModule = sliceModule;
        showSessionExpiredModalAction = sliceModule.showSessionExpiredModal;
      })
      .catch((err) => {
        console.error("Failed to pre-import sessionExpiredSlice:", err);
      });
  }
};

// Initialize on module load
if (typeof window !== 'undefined') {
  // Small delay to ensure store is ready
  setTimeout(initSessionExpiredModal, 100);
  // Also try immediately in case store is already available
  initSessionExpiredModal();
}

function queryParams(source) {
  const array = [];

  for (const key in source) {
    if (source[key] !== null && source[key] !== undefined) {
      array.push(
        encodeURIComponent(key) + "=" + encodeURIComponent(source[key])
      );
    }
  }
  return array.join("&");
}

export default async function callApi(
  endpoint,
  method = "GET",
  payload = {},
  params
) {
  // const token = localStorage.getItem("api_key");
  const token2 = getCookie("API-KEY");
  const token = getCookie("api_key");
  // On localhost use relative URLs so CRA dev-server proxy (package.json) forwards
  // /api/1/* to REACT_APP_API_URL and avoids browser CORS blocks.
  const baseUrl = isLocalhost() ? "" : (process.env.REACT_APP_API_URL || "");

  const isPublicAuthEndpoint =
    endpoint === "/user/login" ||
    endpoint === "/user/forgot" ||
    endpoint === "/user/forgot/send";
  const isLogoutEndpoint = endpoint === "/user/logout";
  const apiHeaders =
    (token || token2) && !isPublicAuthEndpoint ? { "api-key": token || token2 } : {};
  const { extraHeaders = {}, contentType = "", omitBody = false } = params ?? {};

  let fullUrl = `${baseUrl}/api/1${endpoint}`;

  if (method === "GET" && Object.keys(payload).length) {
    fullUrl += `?${queryParams(payload)}`;
  }
  // console.log('apiHeaders', apiHeaders)
  const body =
    contentType === "multipart/form-data" ? payload : JSON.stringify(payload);

  const options = {
    method,
    headers: {
      Accept: "application/json",
      ...(contentType ? {} : { "Content-Type": "application/json" }),
      ...apiHeaders,
      ...extraHeaders,
    },
    body: method !== "GET" && !omitBody ? body : null,
  };

  try {
    const response = await fetch(fullUrl, options);
    // Helper function to show session expired modal
    const showSessionExpiredModal = () => {
      // Use global store reference to avoid circular dependencies
      if (typeof window !== 'undefined' && window.__REDUX_STORE__) {
        // Try to use pre-imported action first
        if (showSessionExpiredModalAction) {
          try {
            window.__REDUX_STORE__.dispatch(showSessionExpiredModalAction());
          } catch (dispatchError) {
            console.error("Failed to dispatch session expired modal action:", dispatchError);
          }
        } else if (sessionExpiredSliceModule) {
          // Use cached module if available
          try {
            showSessionExpiredModalAction = sessionExpiredSliceModule.showSessionExpiredModal;
            window.__REDUX_STORE__.dispatch(sessionExpiredSliceModule.showSessionExpiredModal());
          } catch (dispatchError) {
            console.error("Failed to dispatch session expired modal action:", dispatchError);
          }
        } else {
          // Fallback to async import - but mark flag immediately to prevent multiple imports
          isSessionExpiredModalShown = true;
          import("../redux/features/auth/sessionExpiredSlice")
            .then((sliceModule) => {
              sessionExpiredSliceModule = sliceModule;
              showSessionExpiredModalAction = sliceModule.showSessionExpiredModal;
              if (window.__REDUX_STORE__) {
                window.__REDUX_STORE__.dispatch(sliceModule.showSessionExpiredModal());
              }
            })
            .catch((importError) => {
              console.error("Failed to show session expired modal:", importError);
              // Reset flag on error so it can be retried
              isSessionExpiredModalShown = false;
            });
        }
      } else {
        console.warn("Redux store not available for session expired modal");
      }
    };
    
    // Check for 401 Unauthorized status - show modal only once on first 401 response
    // Don't show modal for login/activate endpoints or on login/invitation pages - it's expected to return 401 for invalid credentials
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const isLoginPage = typeof window !== 'undefined' && (
      pathname.includes('/login') ||
      pathname.includes('/manager') ||
      pathname.includes('/signup') ||
      pathname.includes('/forgot') ||
      pathname.includes('/activation') ||
      pathname.includes('/confirm-2fa') ||
      pathname.startsWith('/join/accept') ||
      pathname.startsWith('/invitation') ||
      pathname.startsWith('/invite/accept') ||
      pathname.startsWith('/invite/profile') ||
      pathname.startsWith('/l/product/') ||
      pathname.startsWith('/l/package/') ||
      pathname.startsWith('/digital-compliance-label/') ||
      // Root path on login domain (e.g. https://login.dev.lappa.org/) redirects to /login but pathname is "/" until redirect
      ((pathname === '/' || pathname === '') && isLoginDomain())
    );
    const isActivateEndpoint = endpoint === '/user/activate';
    const isInvitationEndpoint = endpoint && endpoint.includes('/invitation/validate');

    if (response.status === 401 && !isPublicAuthEndpoint && !isLogoutEndpoint && !isActivateEndpoint && !isInvitationEndpoint && !isLoginPage) {
      // Drop the dead cookie immediately. Otherwise login auto-hops back here and 401s again.
      try {
        clearAuthClientState();
      } catch {
        /* ignore */
      }
      // Use atomic check-and-set to prevent race conditions with multiple simultaneous requests
      if (!isSessionExpiredModalShown) {
        isSessionExpiredModalShown = true;
        showSessionExpiredModal();
      }
    }

    if (!response.ok) {
      response.errorMessage = await getResponseErrorMessage(response);
    }
    
    return response;
  } catch (err) {
    console.error("API call error:", err);
    // Return a mock response object to prevent uncaught errors
    const response = new Response(JSON.stringify({ error: err.message }), {
      status: 500,
      statusText: 'Network Error',
      headers: { 'Content-Type': 'application/json' }
    });
    response.errorMessage = "Error";
    return response;
  }
}

// Export function to reset the flag (useful for testing or after login)
export function resetSessionExpiredFlag() {
  isSessionExpiredModalShown = false;
}
